import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import MultiSelect from "../../../../components/commonComponents/Selectors/MultiSelect";
import { checkUserRole } from "../../../../api_calls/userManagement";
import {
  findAnalyticsCampaignStoreIds,
  getAnalyticsCampaignStoreCatalog,
  intersectWithAnalyticsStoreCatalog,
} from "../../../../api_calls/dashboardFilterSeed";

/**
 * Multi-select Stores filter for campaign analytics.
 * Options and ids come from analytics_campaign_stores_list (not active stores).
 */
const CampaignStoresFilter = ({
  reduxKeywordToSaveValue,
  filterParamRegionName,
  filterParamNielsenRegionCode,
  regionFilterEnabled,
  nielsenRegionFilterEnabled,
}) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const [changesMade, setChangesMade] = React.useState(false);
  const [storesSelection, setStoreSelection] = React.useState([]);
  const [storeNameOptionSelected, setStoreNameOptionsSelected] = React.useState([]);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const [defaultValuesLoaded, setDefaultValuesLoaded] = React.useState(false);
  const [storeList, setStoreList] = React.useState([]);
  const userRole =
    storeDetails?.brand_report_view === true
      ? { user_role: "Brand" }
      : checkUserRole();
  const dispatchAction = useDispatch();

  // Impression vs performance Brand Report bags use separate store lists.
  const brandStoreBagKey =
    reduxKeywordToSaveValue === "embedded_impression_filter"
      ? "impression_filter"
      : "performance_filter";

  const filterStoreOptions = () => {
    // Retailer / Cyreen: all stores on this campaign.
    let campaignStores = getAnalyticsCampaignStoreCatalog();

    if (userRole.user_role === "Brand") {
      // Brand View: only stores shared via Brand Report for this bag.
      const brandStores =
        storeDetails?.brand_filters?.[brandStoreBagKey]?.stores || [];
      campaignStores = intersectWithAnalyticsStoreCatalog(brandStores).sort();
    }

    return campaignStores;
  };

  const storeOptions = React.useMemo(
    () => [...new Set(storeList)].map((n) => ({ value: n, label: n })),
    [storeList]
  );

  React.useEffect(() => {
    setStoreList(filterStoreOptions());
    setDataLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const campaignStoreList = filterStoreOptions();
    if (defaultValuesLoaded === true && campaignStoreList.length !== 0) {
      setStoreList(campaignStoreList);
      const nameOptionsSelected = [...new Set(campaignStoreList)].map((storeName) => ({
        value: storeName,
        label: storeName,
      }));
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: findAnalyticsCampaignStoreIds(campaignStoreList),
            selected_store_names: nameOptionsSelected,
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeDetails?.[reduxKeywordToSaveValue]?.[filterParamRegionName],
    storeDetails?.[reduxKeywordToSaveValue]?.[filterParamNielsenRegionCode],
  ]);

  React.useEffect(() => {
    if (dataLoaded) {
      setStoreSelection(storeList);
      setStoreNameOptionsSelected(
        [...new Set(storeList)].map((storeName) => ({
          value: storeName,
          label: storeName,
        }))
      );
      setDefaultValuesLoaded(true);
    }
  }, [dataLoaded, storeList]);

  return defaultValuesLoaded ? (
    <MultiSelect
      enableFullWidth={false}
      label={"Store"}
      options={storeOptions}
      defaultVal={
        typeof storeDetails[reduxKeywordToSaveValue] !== "undefined" &&
        typeof storeDetails[reduxKeywordToSaveValue].selected_store_names !== "undefined"
          ? storeDetails[reduxKeywordToSaveValue].selected_store_names
          : []
      }
      selectionCheck={(option, value) => option.label === value.label}
      usedFor="storeFilter"
      onChangeCall={(selectedOption) => {
        let userChoices = [];
        setChangesMade(true);
        selectedOption.forEach((chosen) => {
          if (chosen.label !== "(All)") {
            userChoices.push(chosen.label);
          } else {
            userChoices.push([...new Set(storeList)]);
          }
        });
        setStoreSelection(userChoices);
        setStoreNameOptionsSelected(selectedOption);
      }}
      onBlurCall={() => {
        if (storesSelection.length !== 0 && changesMade === true) {
          dispatchAction(
            updateStateData({
              [reduxKeywordToSaveValue]: {
                ...storeDetails[reduxKeywordToSaveValue],
                store_ids: findAnalyticsCampaignStoreIds(storesSelection),
                selected_store_names: storeNameOptionSelected,
              },
            })
          );
          setChangesMade(false);
        }
      }}
    />
  ) : (
    <></>
  );
};

export default CampaignStoresFilter;
